import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  useTheme as useMuiTheme,
  useMediaQuery
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { AttendanceCheckIn, AttendanceHistory } from '../components/attendance';
import { useTheme } from '../context/ThemeContext';
import toast from 'react-hot-toast';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`attendance-tabpanel-${index}`}
      aria-labelledby={`attendance-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// Filter chip component with lighter colors
interface FilterChipProps {
  label: string;
  icon: string;
  isActive: boolean;
  onClick: () => void;
  color?: string;
}

function FilterChip({ label, icon, isActive, onClick, color = '#64748B' }: FilterChipProps) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: 0,
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0
      }}
    >
      <span
        style={{
          padding: '8px 16px',
          fontSize: '13px',
          fontWeight: 600,
          borderRadius: '8px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          display: 'inline-flex',
          alignItems: 'center',
          transition: 'all 0.2s',
          backgroundColor: isActive ? color : '#F1F5F9', /* basil-todo */
          color: isActive ? '#FFFFFF' : '#64748B',
          borderWidth: isActive ? '2px' : '1px',
          borderStyle: 'solid',
          borderColor: isActive ? color : '#CBD5E1', /* basil-todo */
          transform: isActive ? 'scale(1.02)' : 'scale(1)',
          boxShadow: isActive ? `0 2px 4px ${color}30` : 'none'
        }}
      >
        <span style={{ marginRight: '6px', fontSize: '12px', lineHeight: 1 }}>{icon}</span>
        {label}
      </span>
    </button>
  );
}

export default function StewardAttendancePage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [tabValue, setTabValue] = useState(0);

  const handleSuccess = () => {
    toast.success('Attendance recorded successfully!');
  };

  const handleError = (error: string) => {
    toast.error(error);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: theme.background,
        pb: 4
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <button
              onClick={() => navigate('/steward/dashboard')}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 600,
                borderRadius: '6px',
                border: '1px solid var(--border-default)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                backgroundColor: 'var(--bg-sunken)',
                color: 'var(--text-tertiary)',
                transition: 'all 0.2s'
              }}
            >
              <ArrowBack sx={{ fontSize: 16 }} />
              Back
            </button>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Attendance
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Filter Chips - Lighter colors */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          px: { xs: 2, sm: 3 },
          py: 1.5
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, justifyContent: isMobile ? 'center' : 'flex-start' }}>
          <FilterChip
            label="CHECK IN/OUT"
            icon="✓"
            isActive={tabValue === 0}
            onClick={() => setTabValue(0)}
            color="#6366F1"
          />
          <FilterChip
            label="HISTORY"
            icon="📅"
            isActive={tabValue === 1}
            onClick={() => setTabValue(1)}
            color="#64748B"
          />
        </Box>
      </Paper>

      {/* Content */}
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, maxWidth: 800, mx: 'auto' }}>
        <TabPanel value={tabValue} index={0}>
          <AttendanceCheckIn onSuccess={handleSuccess} onError={handleError} />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <AttendanceHistory />
        </TabPanel>
      </Box>
    </Box>
  );
}
